import { formatPesoAmount } from '../utils/money';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getPurchaseById, getProducts, getBranchTaxes } from '../api';
import { jsPDF, autoTable, loadImageAsDataUrl, API_URL } from '../utils/pdfUtils';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';
import { normalizePaginatedResponse } from '../utils/pagination';
import InlineLoading from '../components/InlineLoading';
import { getPurchaseItems } from '../utils/purchaseItems';
import { notify } from '../utils/alertBus';
import useProductEvents from '../hooks/useProductEvents';
import { applyProductEvent } from '../utils/productEventPatch';

const PurchaseQuotePage = () => {
  const { branchId, purchaseId } = useParams();
  const [purchase, setPurchase] = useState(null);
  const [products, setProducts] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const quoteRef = useRef(null);
  const { user } = useCurrentUser();
  const canReadProducts = hasPermission(user, 'product.read');
  const canReadSales = hasPermission(user, 'sales.read');
  const canReadTaxes = hasPermission(user, 'tax.read') || hasPermission(user, 'tax.exclude');

  const refreshProducts = useCallback(async () => {
    const resolvedBranchId = branchId || localStorage.getItem('selectedBranchId');
    if (!resolvedBranchId || !canReadProducts) {
      if (!canReadProducts) {
        setProducts([]);
      }
      return;
    }
    try {
      const productsData = await getProducts(resolvedBranchId, { pageSize: 0 });
      const normalizedProducts = normalizePaginatedResponse(productsData);
      setProducts(normalizedProducts.data || []);
    } catch (err) {
      console.error('Error refreshing products:', err);
    }
  }, [branchId, canReadProducts]);

  const handleProductEvent = useCallback((event) => {
    const nextProducts = applyProductEvent(products, event);
    if (!nextProducts) {
      refreshProducts();
      return;
    }
    if (nextProducts === products) return;
    setProducts(nextProducts);
  }, [products, refreshProducts]);

  useProductEvents(branchId || localStorage.getItem('selectedBranchId'), handleProductEvent);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const resolvedBranchId = branchId || localStorage.getItem('selectedBranchId');
        const [purchaseData, productsData, taxesData] = await Promise.all([
          getPurchaseById(resolvedBranchId, purchaseId),
          canReadProducts ? getProducts(resolvedBranchId, { pageSize: 0 }) : Promise.resolve([]),
          canReadTaxes ? getBranchTaxes(resolvedBranchId) : Promise.resolve([]),
        ]);
        setPurchase(purchaseData);
        const normalizedProducts = normalizePaginatedResponse(productsData);
        setProducts(normalizedProducts.data || []);
        setTaxes(Array.isArray(taxesData) ? taxesData : []);
      } catch (err) {
        console.error('Error fetching data for quote:', err);
        setError('Error al cargar la cotización.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [branchId, purchaseId, canReadProducts, canReadTaxes]);

  const purchaseItems = useMemo(() => getPurchaseItems(purchase), [purchase]);
  const itemsWithRefs = useMemo(() => {
    if (!purchaseItems.length) return [];
    return purchaseItems.map((item) => {
      const product = products.find((p) => p.Varieties?.some((v) => v.id === item.varietyId));
      const variety = product?.Varieties?.find((v) => v.id === item.varietyId);
      const priceObj =
        variety?.VarietyPrices?.find(
          (price) => Number(price.id) === Number(item.varietyPriceId)
        ) ||
        (Number.isInteger(item.priceIndex)
          ? variety?.VarietyPrices?.[item.priceIndex]
          : null);
      const imageSrc =
        priceObj?.priceImage ||
        variety?.varietyImage ||
        product?.productImage ||
        'https://placehold.co/60';

      const effectivePrice = item.price - (item.discount || 0);
      const subtotal = Math.floor(effectivePrice * item.quantity);
      const taxAmount = Number.isFinite(Number(item.taxAmount)) ? Number(item.taxAmount) : null;
      const netAmount =
        Number.isFinite(Number(item.netAmount))
          ? Number(item.netAmount)
          : taxAmount !== null
            ? subtotal - taxAmount
            : subtotal;
      const totalAmount =
        Number.isFinite(Number(item.netAmount)) && taxAmount !== null
          ? netAmount + taxAmount
          : subtotal;

      return {
        imageSrc,
        productName: product?.productName || item.varietyName,
        varietyName: item.varietyName,
        metricUnit: item.metricUnit,
        quantity: item.quantity,
        price: item.price,
        discount: item.discount || 0,
        effectivePrice,
        subtotal,
        netAmount,
        totalAmount,
      };
    });
  }, [purchaseItems, products]);

  if (loading) {
    return (
      <p className="p-4 text-center">
        <InlineLoading label="Cargando cotización" />
      </p>
    );
  }

  if (error) {
    return <p className="p-4 text-center text-red-600">{error}</p>;
  }

  if (!purchase) {
    return <p className="p-4 text-center">No se encontró la cotización.</p>;
  }

  const excludedTaxIds = Array.isArray(purchase.excludedTaxIds) ? purchase.excludedTaxIds : [];
  const taxNameById = new Map(
    (taxes || []).map((tax) => [Number(tax.id), tax.name || `Impuesto ${tax.id}`])
  );
  const excludedLabels = excludedTaxIds
    .map((id) => taxNameById.get(Number(id)) || `#${id}`)
    .filter(Boolean);
  const excludedNote =
    excludedLabels.length > 0 ? `Impuestos excluidos: ${excludedLabels.join(', ')}` : null;

  const handleDownloadPdf = async () => {
    try {
      if (!canReadSales) return;
      // Preconvert the table images to data URLs so they can be embedded safely
      const tableElement = document.getElementById('quote-table');
      if (tableElement) {
        const imgElements = Array.from(tableElement.querySelectorAll('tbody img'));
        await Promise.all(
          imgElements.map(async (img) => {
            if (img.getAttribute('data-pdf-src')) return;
            const dataUrl = await loadImageAsDataUrl(img.src);
            if (dataUrl) {
              img.setAttribute('data-pdf-src', dataUrl);
              const format = dataUrl.includes('image/png') ? 'PNG' : 'JPEG';
              img.setAttribute('data-pdf-format', format);
            }
          })
        );
      }

      const doc = new jsPDF('p', 'pt', 'a4');
      const marginX = 40;
      let cursorY = 48;

      // Centered, slightly smaller title
      const pageWidth = doc.internal.pageSize.getWidth();
      const title = 'Cotización';
      doc.setFontSize(16);
      const titleWidth = doc.getTextWidth(title);
      const titleX = (pageWidth - titleWidth) / 2;
      doc.text(title, titleX, cursorY);
      cursorY += 24;

      if (purchase.Client) {
        doc.setFontSize(11);
        doc.text(`Cliente: ${purchase.Client.name}`, marginX, cursorY);
        cursorY += 16;
        if (purchase.Client.rut) {
          doc.text(`RUT: ${purchase.Client.rut}`, marginX, cursorY);
          cursorY += 14;
        }
        if (purchase.Client.address || purchase.Client.city) {
          doc.text(
            `Dirección: ${purchase.Client.address || ''}${purchase.Client.city ? `, ${purchase.Client.city}` : ''}`,
            marginX,
            cursorY
          );
          cursorY += 14;
        }
        cursorY += 6;
      }

      autoTable(doc, {
        startY: cursorY,
        margin: { left: marginX, right: marginX },
        // Let jsPDF-AutoTable parse the existing HTML table
        html: '#quote-table',
        styles: { fontSize: 10, cellPadding: 6, valign: 'middle' },
        didDrawCell: (data) => {
          // Draw thumbnail in first column (Imagen)
          if (data.cell.section === 'body' && data.column.index === 0) {
            const td = data.cell.raw;
            const img = td?.getElementsByTagName('img')[0];
            if (!img) return;

            const src = img.getAttribute('data-pdf-src') || img.src;
            if (!src || !src.startsWith('data:image')) return;

            const cell = data.cell;
            const dim = cell.height - cell.padding('vertical');
            const x = cell.x + cell.padding('left');
            const y = cell.y + cell.padding('top');

            const formatAttr = img.getAttribute('data-pdf-format');
            const format =
              formatAttr || (src.includes('image/png') ? 'PNG' : 'JPEG');

            try {
              doc.addImage(src, format, x, y, dim, dim);
            } catch (e) {
              console.warn('No se pudo agregar la imagen al PDF:', e);
            }
          }

          // No special handling for text in other columns; AutoTable will wrap it
        },
      });

      const clientName = purchase.Client?.name?.replace(/\s+/g, '_') || 'cliente';
      const fileName = `cotizacion_${clientName}_${purchaseId}.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error('Error generating PDF:', err);
      notify('No se pudo generar el PDF.');
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-end mb-3">
          {canReadSales && (
            <button
              onClick={handleDownloadPdf}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
            >
              Descargar PDF
            </button>
          )}
        </div>
        <div
          ref={quoteRef}
          className="bg-white rounded-lg shadow p-6"
          style={{
            maxWidth: 840,
            margin: '0 auto',
          }}
        >
          <h1 className="text-2xl font-bold mb-6 text-center">Cotización</h1>

          {purchase.Client && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-1">Cliente</h2>
              <p className="text-sm text-gray-700">
                {purchase.Client.name}
                {purchase.Client.rut && ` — ${purchase.Client.rut}`}
              </p>
              {(purchase.Client.address || purchase.Client.city) && (
                <p className="text-sm text-gray-600">
                  {purchase.Client.address}
                  {purchase.Client.city && `, ${purchase.Client.city}`}
                </p>
              )}
            </div>
          )}

          <div className="overflow-x-auto">
            <table
              id="quote-table"
              className="min-w-full divide-y divide-gray-200"
            >
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Imagen
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Producto
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Variedad
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cantidad
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Precio
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor neto
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {itemsWithRefs.map((row, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-3">
                      <img
                        src={row.imageSrc}
                        alt={row.varietyName}
                        className="w-12 h-12 object-cover rounded"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                      {row.productName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.varietyName || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {row.quantity} x {row.metricUnit || 'unidad'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {row.discount ? (
                        <>
                          <span className="line-through mr-1">${formatPesoAmount(row.price)}</span>
                          <span>${formatPesoAmount(row.effectivePrice)}</span>
                        </>
                      ) : (
                        `$${formatPesoAmount(row.price)}`
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap">
                      ${formatPesoAmount(row.netAmount)}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap">
                      ${formatPesoAmount(row.totalAmount)}
                    </td>
                  </tr>
                ))}
                {itemsWithRefs.length === 0 && (
                  <tr>
                    <td className="px-4 py-6 text-center text-sm text-gray-500" colSpan={7}>
                      No hay productos en esta cotización.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5}></td>
                  <td className="px-4 py-3 text-right text-sm font-semibold">Subtotal</td>
                  <td className="px-4 py-3 text-sm font-semibold">
                    {(() => {
                      const itemsSubtotal =
                        typeof purchase.itemsSubtotal === 'number'
                          ? purchase.itemsSubtotal
                          : purchaseItems.reduce((sum, item) => {
                              if (!item || item.quantity <= 0) return sum;
                              const unit = (Number(item.price) || 0) - (Number(item.discount) || 0);
                              const baseTotal = Math.floor(unit * item.quantity);
                              const taxAmount = Number.isFinite(Number(item.taxAmount))
                                ? Number(item.taxAmount)
                                : null;
                              const netAmount = Number.isFinite(Number(item.netAmount))
                                ? Number(item.netAmount)
                                : taxAmount !== null
                                  ? baseTotal - taxAmount
                                  : baseTotal;
                              const lineTotal =
                                Number.isFinite(Number(item.netAmount)) && taxAmount !== null
                                  ? netAmount + taxAmount
                                  : baseTotal;
                              return sum + lineTotal;
                            }, 0);
                      return `$${formatPesoAmount(itemsSubtotal)}`;
                    })()}
                  </td>
                </tr>
                {(() => {
                  const netTotal = Number.isFinite(Number(purchase.netTotal))
                    ? Number(purchase.netTotal)
                    : null;
                  const breakdown = Array.isArray(purchase.taxBreakdown) ? purchase.taxBreakdown : [];
                  const taxTotal = breakdown.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
                  const resolvedNet = netTotal ?? Math.max(Number(purchase.totalAmount || 0) - taxTotal, 0);
                  return (
                    <>
                      <tr>
                        <td colSpan={5}></td>
                        <td className="px-4 py-1 text-right text-sm font-semibold">Valor neto</td>
                        <td className="px-4 py-1 text-sm font-semibold">
                          ${formatPesoAmount(resolvedNet)}
                        </td>
                      </tr>
                      {breakdown.map((row) => (
                        <tr key={row.taxId || row.name}>
                          <td colSpan={5}></td>
                          <td className="px-4 py-1 text-right text-sm font-semibold">
                            {row.name || 'Impuesto'} {row.rate ? `(${row.rate}%)` : ''}
                          </td>
                          <td className="px-4 py-1 text-sm font-semibold">
                            ${formatPesoAmount(Number(row.amount || 0))}
                          </td>
                        </tr>
                      ))}
                      {excludedNote && (
                        <tr>
                          <td colSpan={7} className="px-4 py-1 text-xs text-gray-600">
                            {excludedNote}
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })()}
                {(() => {
                  const percentNum = Number(purchase.orderDiscountPercentage);
                  const percent =
                    Number.isFinite(percentNum)
                      ? percentNum
                      : null;
                  const itemsSubtotal =
                    typeof purchase.itemsSubtotal === 'number'
                      ? purchase.itemsSubtotal
                      : purchaseItems.reduce((sum, item) => {
                          if (!item || item.quantity <= 0) return sum;
                          const unit = (Number(item.price) || 0) - (Number(item.discount) || 0);
                          const baseTotal = Math.floor(unit * item.quantity);
                          const taxAmount = Number.isFinite(Number(item.taxAmount))
                            ? Number(item.taxAmount)
                            : null;
                          const netAmount = Number.isFinite(Number(item.netAmount))
                            ? Number(item.netAmount)
                            : taxAmount !== null
                              ? baseTotal - taxAmount
                              : baseTotal;
                          const lineTotal =
                            Number.isFinite(Number(item.netAmount)) && taxAmount !== null
                              ? netAmount + taxAmount
                              : baseTotal;
                          return sum + lineTotal;
                        }, 0);
                  const amountNum = Number(purchase.orderDiscountAmount);
                  const effectiveOrderDiscount =
                    percent !== null
                      ? Math.floor(itemsSubtotal * (percent / 100))
                      : Number.isFinite(amountNum)
                        ? amountNum
                        : 0;
                  if (effectiveOrderDiscount <= 0) return null;
                  return (
                    <tr>
                      <td colSpan={5}></td>
                      <td className="px-4 py-1 text-right text-sm font-semibold text-red-600">
                        Descuento orden
                        {percent != null ? ` (${percent}%)` : ''}
                      </td>
                      <td className="px-4 py-1 text-sm font-semibold text-red-600">
                        -${formatPesoAmount(effectiveOrderDiscount)}
                      </td>
                    </tr>
                  );
                })()}
                <tr>
                  <td colSpan={5}></td>
                  <td className="px-4 py-3 text-right text-sm font-semibold">Total</td>
                  <td className="px-4 py-3 text-sm font-bold text-blue-600">
                    ${formatPesoAmount(purchase.totalAmount)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseQuotePage;
